import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { ArrowLeft, ScanLine, CheckCircle2, LogOut, Users, Clock, AlertTriangle, X, Keyboard, Printer, Radio, Play, Square, Plus, UserCheck, DoorOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { getAdminHeaders } from '../lib/auth';
import EtiquetaCheckin from '../components/EtiquetaCheckin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckinVoluntario = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [active, setActive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manualCode, setManualCode] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [labelData, setLabelData] = useState(null);
  const [meeting, setMeeting] = useState(null);
  const [activeMeetings, setActiveMeetings] = useState([]);
  const [meetingLoading, setMeetingLoading] = useState(false);
  const [showAvulso, setShowAvulso] = useState(false);
  const [avulsoTitle, setAvulsoTitle] = useState('');
  const [nextAgenda, setNextAgenda] = useState(null);
  const [canStart, setCanStart] = useState(false);
  const [startMessage, setStartMessage] = useState('');
  const [pendingModal, setPendingModal] = useState(null); // {meetingId, pending, message}
  const [forceConfirmText, setForceConfirmText] = useState('');
  const [volunteerModal, setVolunteerModal] = useState(false);
  const [childrenList, setChildrenList] = useState([]);
  const [childSearch, setChildSearch] = useState('');
  const [selectedChildForVol, setSelectedChildForVol] = useState('');
  const [volObs, setVolObs] = useState('');
  const scannerRef = useRef(null);

  useEffect(() => { fetchActive(); fetchMeeting(); }, []);

  useEffect(() => {
    return () => { stopScanner(); };
  }, []);

  // Poll meeting status every 30s to update can_start
  useEffect(() => {
    if (meeting) return;
    const interval = setInterval(fetchMeeting, 30000);
    return () => clearInterval(interval);
  }, [meeting]);

  const fetchMeeting = async () => {
    try {
      const res = await axios.get(`${API}/checkin/meeting-status`);
      setMeeting(res.data.active ? res.data.meeting : null);
      setNextAgenda(res.data.next_agenda);
      setCanStart(res.data.can_start);
      setStartMessage(res.data.start_message || '');
      // Fetch all active meetings
      try {
        const allRes = await axios.get(`${API}/checkin/active-meetings`, { headers: getAdminHeaders() });
        setActiveMeetings(allRes.data || []);
      } catch { }
    } catch { }
  };

  const handleStartMeeting = async (type = 'regular', title = '') => {
    setMeetingLoading(true);
    try {
      const res = await axios.post(`${API}/checkin/start-meeting`, { type, title }, { headers: getAdminHeaders() });
      setMeeting(res.data);
      setShowAvulso(false);
      setAvulsoTitle('');
      toast.success('Encontro iniciado!');
      fetchMeeting();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao iniciar'); }
    setMeetingLoading(false);
  };

  const handleEndMeeting = async (meetingId = null, force = false) => {
    setMeetingLoading(true);
    try {
      const body = {};
      if (meetingId) body.meeting_id = meetingId;
      if (force) body.force = true;
      const res = await axios.post(`${API}/checkin/end-meeting`, body, { headers: getAdminHeaders() });
      toast.success(res.data.message || 'Encontro finalizado!');
      if (res.data.auto_checked_out > 0) {
        toast.info(`${res.data.auto_checked_out} criança(s) saíram em auto-checkout`);
      }
      // Offer to navigate to WhatsApp report
      if (res.data.whatsapp_report_url && res.data.meeting_id) {
        setTimeout(() => {
          if (window.confirm('Encontro finalizado! Deseja gerar o relatório WhatsApp para os líderes de Discipulado agora?')) {
            navigate(res.data.whatsapp_report_url);
          }
        }, 500);
      }
      if (activeMeetings.length <= 1) setMeeting(null);
      setPendingModal(null);
      setForceConfirmText('');
      fetchActive();
      fetchMeeting();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.can_force) {
        setPendingModal({ meetingId, pending: detail.pending || [], message: detail.message });
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Erro ao finalizar'));
      }
    }
    setMeetingLoading(false);
  };

  const handleManualCheckout = async (checkinItem) => {
    if (!window.confirm(`Confirmar checkout manual de ${checkinItem.child_name}?`)) return;
    try {
      await axios.post(`${API}/checkin/manual-checkout/${checkinItem.id}`, {}, { headers: getAdminHeaders() });
      toast.success(`${checkinItem.child_name} - Checkout manual realizado`);
      fetchActive();
      setPendingModal(prev => prev ? { ...prev, pending: prev.pending.filter(p => p.id !== checkinItem.id) } : null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao fazer checkout'); }
  };

  const openVolunteerModal = async () => {
    try {
      const res = await axios.get(`${API}/checkin/children-list`, { headers: getAdminHeaders() });
      setChildrenList(res.data || []);
      setVolunteerModal(true);
    } catch (e) { toast.error('Erro ao carregar lista de crianças'); }
  };

  const handleVolunteerGenerate = async () => {
    if (!selectedChildForVol) { toast.error('Selecione uma criança'); return; }
    try {
      const res = await axios.post(`${API}/checkin/volunteer-generate`,
        { child_id: selectedChildForVol, observacoes: volObs },
        { headers: getAdminHeaders() });
      toast.success(`Check-in criado! Token: ${res.data.token}`);
      // Auto scan entry to mark as present immediately
      await processCode(res.data.qr_code, 'entrada');
      // Notify family via WhatsApp (open deep links)
      const links = res.data.whatsapp_links || [];
      if (links.length > 0) {
        links.forEach((link, i) => setTimeout(() => window.open(link, '_blank'), i * 500));
        toast.success(`Avisando ${links.length} responsavel(is) via WhatsApp`);
      } else {
        toast.info('Familia sem celular cadastrado - notificacao nao enviada');
      }
      setVolunteerModal(false);
      setSelectedChildForVol('');
      setVolObs('');
      setChildSearch('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao gerar check-in'); }
  };

  const fetchActive = async () => {
    try {
      const res = await axios.get(`${API}/checkin/active`, { headers: getAdminHeaders() });
      setActive(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const startScanner = async (scanMode) => {
    setMode(scanMode);
    setScanning(true);
    setResult(null);
  };

  // Start camera only after the #qr-reader div is rendered
  useEffect(() => {
    if (!scanning || !mode) return;
    let cancelled = false;

    const initCamera = async () => {
      // Wait for DOM element to be available
      await new Promise(r => setTimeout(r, 400));
      const el = document.getElementById('qr-reader');
      if (!el || cancelled) return;

      try {
        // Request camera permission explicitly first
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" }
        });
        // Stop the stream immediately - we just needed to trigger the permission
        stream.getTracks().forEach(t => t.stop());

        if (cancelled) return;

        const html5QrCode = new Html5Qrcode("qr-reader");
        scannerRef.current = html5QrCode;

        await html5QrCode.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0,
          },
          async (decodedText) => {
            try { await html5QrCode.stop(); } catch {}
            scannerRef.current = null;
            if (!cancelled) {
              setScanning(false);
              await processCode(decodedText, mode);
            }
          },
          () => {}
        );
      } catch (err) {
        console.error("Scanner error:", err);
        if (!cancelled) {
          setScanning(false);
          setShowManual(true);
          if (err.name === 'NotAllowedError') {
            toast.error('Permissao de camera negada. Ative nas configuracoes do navegador.');
          } else if (err.name === 'NotFoundError') {
            toast.error('Camera nao encontrada neste dispositivo.');
          } else {
            toast.error('Erro ao acessar camera. Use o modo manual.');
          }
        }
      }
    };

    initCamera();
    return () => { cancelled = true; };
  }, [scanning, mode]);

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const processCode = async (code, scanMode) => {
    try {
      const endpoint = scanMode === 'entrada' ? '/checkin/scan-entry' : '/checkin/scan-exit';
      const res = await axios.post(`${API}${endpoint}`, { qr_code: code }, { headers: getAdminHeaders() });
      setResult(res.data);
      toast.success(res.data.message);
      fetchActive();
      // Auto-show label for entry
      if (scanMode === 'entrada' && res.data.label) {
        setLabelData(res.data.label);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao processar');
      setResult({ error: e.response?.data?.detail || 'Erro' });
    }
  };

  const handleManualSubmit = async () => {
    if (!manualCode.trim()) return;
    await processCode(manualCode.trim(), mode);
    setManualCode('');
    setShowManual(false);
  };

  const handleReprintLabel = async (checkinItem) => {
    try {
      const res = await axios.get(`${API}/checkin/label/${checkinItem.id}`, { headers: getAdminHeaders() });
      setLabelData(res.data);
    } catch (e) { toast.error('Erro ao carregar etiqueta'); }
  };

  const stats = {
    pending: active.filter(c => c.status === 'pending').length,
    present: active.filter(c => c.status === 'checked_in').length,
    checkout: active.filter(c => c.status === 'checkout_requested').length,
    done: active.filter(c => c.status === 'checked_out').length,
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <ScanLine className="w-7 h-7" />
            <h1 className="font-nunito font-bold text-3xl">Check-in / Check-out</h1>
          </div>
          <p className="font-inter text-white/70 text-sm mt-1">Escaneie o QR Code das crianças</p>
          <div className="flex items-center gap-2 mt-2">
            <button onClick={() => navigate('/encontro-ao-vivo')} data-testid="live-dashboard-btn"
              className="bg-white/10 hover:bg-white/20 text-white rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 transition-all">
              <Radio className="w-3.5 h-3.5" /> Ao Vivo
            </button>
            <button onClick={() => navigate('/checkin/impressora')} data-testid="print-station-btn"
              className="bg-white/10 hover:bg-white/20 text-white rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 transition-all">
              <Printer className="w-3.5 h-3.5" /> Estacao de Impressao
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-3">
          <Card className="bg-amber-50 rounded-xl p-3 text-center border border-amber-200">
            <p className="font-nunito font-bold text-2xl text-amber-700">{stats.pending}</p>
            <p className="text-[10px] text-amber-600 font-semibold">Aguardando</p>
          </Card>
          <Card className="bg-green-50 rounded-xl p-3 text-center border border-green-200">
            <p className="font-nunito font-bold text-2xl text-green-700">{stats.present}</p>
            <p className="text-[10px] text-green-600 font-semibold">Presentes</p>
          </Card>
          <Card className="bg-blue-50 rounded-xl p-3 text-center border border-blue-200">
            <p className="font-nunito font-bold text-2xl text-blue-700">{stats.checkout}</p>
            <p className="text-[10px] text-blue-600 font-semibold">Saída pedida</p>
          </Card>
          <Card className="bg-gray-50 rounded-xl p-3 text-center border border-gray-200">
            <p className="font-nunito font-bold text-2xl text-gray-600">{stats.done}</p>
            <p className="text-[10px] text-gray-500 font-semibold">Saíram</p>
          </Card>
        </div>

        {/* Meeting Control */}
        {!meeting ? (
          <Card className="bg-white rounded-2xl p-5 shadow-lg border-2 border-dashed border-amber-300" data-testid="meeting-control">
            {/* Next agenda info */}
            {nextAgenda && (
              <div className="bg-[#F2F7F4] rounded-xl p-3 mb-4">
                <p className="font-inter text-[10px] text-gray-500 uppercase font-semibold mb-1">Proximo encontro na agenda</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-nunito font-bold text-sm text-[#1A4D2E]">
                      Semana {nextAgenda.semana} - {nextAgenda.data ? new Date(nextAgenda.data + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' }) : ''}
                    </p>
                    {nextAgenda.tema && <p className="font-inter text-xs text-gray-600">{nextAgenda.tema}</p>}
                  </div>
                  <span className="font-mono text-sm font-bold text-[#1A4D2E]">{nextAgenda.horario_inicio} - {nextAgenda.horario_fim}</span>
                </div>
              </div>
            )}

            <div className="text-center mb-4">
              <AlertTriangle className="w-7 h-7 text-amber-500 mx-auto mb-1" />
              <p className="font-nunito font-bold text-sm text-gray-700">Nenhum encontro ativo</p>
              {!canStart && startMessage && (
                <p className="font-inter text-xs text-amber-600 mt-1">{startMessage}</p>
              )}
              {canStart && <p className="font-inter text-xs text-green-600 mt-1">Liberado para iniciar!</p>}
            </div>

            {!showAvulso ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button onClick={() => handleStartMeeting('regular')} disabled={meetingLoading || !canStart} data-testid="start-regular-btn"
                  className="bg-[#1A4D2E] hover:bg-[#163f25] disabled:opacity-40 text-white py-4 rounded-xl font-nunito font-bold text-sm transition-all flex items-center justify-center gap-2">
                  <Play className="w-5 h-5" /> Iniciar Encontro
                </button>
                <button onClick={() => setShowAvulso(true)} data-testid="start-avulso-toggle"
                  className="bg-amber-500 hover:bg-amber-600 text-white py-4 rounded-xl font-nunito font-bold text-sm transition-all flex items-center justify-center gap-2">
                  <Plus className="w-5 h-5" /> Encontro Avulso
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <input
                  value={avulsoTitle}
                  onChange={e => setAvulsoTitle(e.target.value)}
                  placeholder="Nome do encontro (ex: Evento Especial)"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-amber-500 outline-none"
                  data-testid="avulso-title-input"
                />
                <div className="flex gap-3">
                  <button onClick={() => setShowAvulso(false)} className="flex-1 py-3 rounded-xl text-sm text-gray-500 border border-gray-200">Cancelar</button>
                  <button onClick={() => handleStartMeeting('avulso', avulsoTitle)} disabled={meetingLoading || !avulsoTitle.trim()} data-testid="start-avulso-btn"
                    className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2">
                    <Play className="w-4 h-4" /> Iniciar
                  </button>
                </div>
              </div>
            )}
          </Card>
        ) : (
          <div className="space-y-2">
            {activeMeetings.map((m) => (
              <Card key={m.id} className="bg-green-50 rounded-2xl p-4 shadow-lg border-2 border-green-400" data-testid={`meeting-active-${m.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                    </div>
                    <div>
                      <p className="font-nunito font-bold text-sm text-green-800">{m.title}</p>
                      <p className="font-inter text-[10px] text-green-600">Iniciado por {m.started_by} - {m.started_at ? new Date(m.started_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''}</p>
                    </div>
                  </div>
                  <button onClick={() => handleEndMeeting(m.id)} disabled={meetingLoading} data-testid={`end-meeting-btn-${m.id}`}
                    className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all">
                    <Square className="w-3.5 h-3.5" /> Finalizar
                  </button>
                </div>
              </Card>
            ))}
            {/* Allow starting additional meetings */}
            <button onClick={() => setShowAvulso(true)} className="w-full text-sm text-green-600 font-semibold py-2 hover:underline" data-testid="add-meeting-btn">
              + Iniciar outro encontro
            </button>
            {showAvulso && (
              <Card className="bg-white rounded-xl p-3 border border-gray-200">
                <input value={avulsoTitle} onChange={e => setAvulsoTitle(e.target.value)} placeholder="Nome do encontro"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2" data-testid="avulso-title-extra" />
                <div className="flex gap-2">
                  <button onClick={() => setShowAvulso(false)} className="flex-1 py-2 rounded-lg text-xs text-gray-500 border border-gray-200">Cancelar</button>
                  <button onClick={() => handleStartMeeting('avulso', avulsoTitle)} disabled={!avulsoTitle.trim()}
                    className="flex-1 bg-amber-500 text-white py-2 rounded-lg font-bold text-xs disabled:opacity-50">Iniciar</button>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Action Buttons */}
        {meeting && !scanning && !result && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <button onClick={() => startScanner('entrada')} data-testid="scan-entry-btn"
                className="bg-green-600 hover:bg-green-700 text-white py-6 rounded-2xl font-nunito font-bold text-lg transition-all flex flex-col items-center gap-2 shadow-lg">
                <ScanLine className="w-8 h-8" /> Escanear Entrada
              </button>
              <button onClick={() => startScanner('saida')} data-testid="scan-exit-btn"
                className="bg-red-500 hover:bg-red-600 text-white py-6 rounded-2xl font-nunito font-bold text-lg transition-all flex flex-col items-center gap-2 shadow-lg">
                <LogOut className="w-8 h-8" /> Escanear Saída
              </button>
            </div>
            <button onClick={openVolunteerModal} data-testid="volunteer-checkin-btn"
              className="w-full bg-amber-500 hover:bg-amber-600 text-white py-4 rounded-2xl font-nunito font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-md">
              <UserCheck className="w-5 h-5" /> Check-in de Criança Desacompanhada
            </button>
          </>
        )}

        {/* Scanner */}
        {scanning && (
          <Card className="bg-white rounded-2xl p-6 shadow-lg border border-[#E1ECE5]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-nunito font-bold text-lg text-[#1A4D2E]">
                {mode === 'entrada' ? 'Escaneando Entrada' : 'Escaneando Saida'}
              </h3>
              <button onClick={stopScanner} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div id="qr-reader" className="w-full rounded-xl overflow-hidden" style={{ minHeight: '300px' }} />
            <button onClick={() => { stopScanner(); setShowManual(true); }}
              className="mt-4 w-full text-gray-500 hover:text-[#1A4D2E] py-2 text-sm font-inter flex items-center justify-center gap-2">
              <Keyboard className="w-4 h-4" /> Digitar codigo manualmente
            </button>
          </Card>
        )}

        {/* Manual input */}
        {showManual && !scanning && (
          <Card className="bg-white rounded-2xl p-6 shadow-lg border border-[#E1ECE5]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-nunito font-bold text-lg text-[#1A4D2E]">
                {mode === 'entrada' ? 'Entrada Manual' : 'Saída Manual'}
              </h3>
              <button onClick={() => setShowManual(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <p className="font-inter text-sm text-gray-500 mb-3">Digite o token de 4 digitos ou o codigo QR completo:</p>
            <div className="flex gap-3">
              <input value={manualCode} onChange={e => setManualCode(e.target.value)}
                className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-lg font-mono text-center tracking-[0.1em] focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent outline-none"
                placeholder="0000 ou CHECKIN-..." data-testid="manual-code-input" autoFocus />
              <button onClick={handleManualSubmit} data-testid="manual-submit-btn"
                className="bg-[#1A4D2E] hover:bg-[#163f25] text-white px-6 py-3 rounded-xl font-semibold transition-all">
                OK
              </button>
            </div>
          </Card>
        )}

        {/* Result */}
        {result && !result.error && (
          <Card className={`rounded-2xl p-6 shadow-lg border-2 ${mode === 'entrada' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-3 rounded-full ${mode === 'entrada' ? 'bg-green-500' : 'bg-blue-500'}`}>
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-inter text-sm text-gray-600">{mode === 'entrada' ? 'Entrada confirmada' : 'Saída confirmada'}</p>
                <h2 className="font-nunito font-bold text-2xl text-[#1A4D2E]">{result.checkin?.child_name}</h2>
              </div>
            </div>

            {result.checkin?.observacoes && (
              <div className="bg-white/80 rounded-xl p-3 mb-3">
                <p className="font-inter text-xs text-amber-700 font-semibold mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Observações</p>
                <p className="font-inter text-sm text-amber-800">{result.checkin.observacoes}</p>
              </div>
            )}

            {mode === 'entrada' && result.pontualidade !== undefined && (
              <div className={`rounded-xl p-3 text-center ${result.pontualidade ? 'bg-green-100' : 'bg-amber-100'}`}>
                <p className="font-inter text-sm font-semibold">{result.pontualidade ? 'Pontual - ponto registrado!' : 'Chegou apos 16:40 - sem ponto de pontualidade'}</p>
              </div>
            )}

            {mode === 'entrada' && result.label && (
              <button onClick={() => setLabelData(result.label)} data-testid="open-label-btn"
                className="w-full mt-2 bg-amber-500 hover:bg-amber-600 text-white py-3 rounded-xl font-nunito font-bold text-sm transition-all flex items-center justify-center gap-2">
                <Printer className="w-5 h-5" /> Imprimir Etiqueta
              </button>
            )}

            {mode === 'saida' && result.responsavel && (
              <div className="bg-white/80 rounded-xl p-3">
                <p className="font-inter text-xs text-gray-500 mb-1">Retirado por</p>
                <p className="font-nunito font-bold text-[#1A4D2E]">{result.responsavel.nome}</p>
                {result.responsavel.celular && <p className="font-inter text-xs text-gray-400">{result.responsavel.celular}</p>}
              </div>
            )}

            <button onClick={() => { setResult(null); setMode(null); }} data-testid="scan-another-btn"
              className="mt-4 w-full bg-[#1A4D2E] hover:bg-[#163f25] text-white py-3 rounded-xl font-nunito font-bold text-sm transition-all">
              Escanear Outro
            </button>
          </Card>
        )}

        {result?.error && (
          <Card className="bg-red-50 rounded-2xl p-6 border-2 border-red-200 text-center">
            <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="font-nunito font-bold text-red-800">{result.error}</p>
            <button onClick={() => { setResult(null); setMode(null); }}
              className="mt-4 text-red-600 hover:text-red-700 text-sm font-semibold">
              Tentar novamente
            </button>
          </Card>
        )}

        {/* Active checkins list */}
        <div>
          <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-3 flex items-center gap-2">
            <Users className="w-5 h-5" /> Crianças Hoje ({active.length})
          </h3>
          {active.length === 0 ? (
            <p className="font-inter text-sm text-gray-400 text-center py-4">Nenhum check-in hoje</p>
          ) : (
            <div className="space-y-2">
              {active.map(c => (
                <Card key={c.id} className="bg-white rounded-xl p-3 shadow-sm border border-[#E1ECE5] flex items-center justify-between" data-testid={`active-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-3 h-3 rounded-full ${
                      c.status === 'checked_in' ? 'bg-green-500' :
                      c.status === 'checkout_requested' ? 'bg-blue-500 animate-pulse' :
                      c.status === 'pending' ? 'bg-amber-400' : 'bg-gray-300'
                    }`} />
                    <div>
                      <p className="font-nunito font-bold text-sm text-[#1A4D2E]">{c.child_name}</p>
                      <p className="font-inter text-[10px] text-gray-400">
                        {c.checkin_time ? new Date(c.checkin_time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : 'Aguardando'}
                        {c.parent_name && ` - ${c.parent_name}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.status === 'checked_in' && (
                      <button onClick={() => handleReprintLabel(c)} data-testid={`reprint-${c.id}`}
                        className="text-amber-500 hover:text-amber-600 hover:bg-amber-50 rounded-full p-1.5 transition-all" title="Reimprimir etiqueta">
                        <Printer className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {(c.status === 'checked_in' || c.status === 'checkout_requested') && (
                      <button onClick={() => handleManualCheckout(c)} data-testid={`manual-checkout-${c.id}`}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 rounded-full p-1.5 transition-all" title="Checkout manual (sem QR)">
                        <DoorOpen className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {c.observacoes && (
                      <span className="bg-amber-100 text-amber-600 px-2 py-0.5 rounded text-[10px] font-semibold" title={c.observacoes}>Obs</span>
                    )}
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      c.status === 'checked_in' ? 'bg-green-100 text-green-700' :
                      c.status === 'checkout_requested' ? 'bg-blue-100 text-blue-700' :
                      c.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {c.status === 'checked_in' ? 'Presente' : c.status === 'checkout_requested' ? 'Saída pedida' : c.status === 'pending' ? 'Aguardando' : 'Saiu'}
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Label Print Modal */}
      {labelData && (
        <EtiquetaCheckin label={labelData} onClose={() => setLabelData(null)} />
      )}

      {/* Pending Children Modal (when end-meeting is blocked) */}
      {pendingModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="pending-modal">
          <Card className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto shadow-2xl">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <h3 className="font-nunito font-bold text-lg text-[#1A4D2E]">Crianças sem checkout</h3>
              </div>
              <button onClick={() => { setPendingModal(null); setForceConfirmText(''); }} className="text-gray-400 hover:text-gray-600" data-testid="close-pending-modal">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-600">
                <strong>{pendingModal.pending.length}</strong> criança(s) ainda estão marcadas como presentes. Faça o checkout de cada uma ou force o encerramento abaixo.
              </p>
              <div className="space-y-2">
                {pendingModal.pending.map(c => (
                  <div key={c.id} className="flex items-center justify-between bg-amber-50 rounded-xl p-3 border border-amber-200" data-testid={`pending-item-${c.id}`}>
                    <div>
                      <p className="font-nunito font-bold text-sm text-[#1A4D2E]">{c.child_name}</p>
                      <p className="text-[10px] text-gray-500">{c.child_codigo} · {c.parent_name}</p>
                    </div>
                    <button onClick={() => handleManualCheckout(c)} data-testid={`modal-manual-checkout-${c.id}`}
                      className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1">
                      <DoorOpen className="w-3.5 h-3.5" /> Checkout
                    </button>
                  </div>
                ))}
              </div>
              {pendingModal.pending.length === 0 && (
                <div className="bg-green-50 rounded-xl p-3 border border-green-200 text-center">
                  <p className="text-sm text-green-700 font-semibold">Todas as crianças saíram! Pode encerrar agora.</p>
                </div>
              )}

              {/* Force close section */}
              <div className="pt-4 border-t border-dashed border-gray-200">
                <p className="text-xs text-gray-500 font-semibold mb-2">OU FORÇAR ENCERRAMENTO:</p>
                <p className="text-xs text-red-600 mb-2">⚠ Isto fará auto-checkout de todas as crianças pendentes. Use apenas se elas já saíram sem escanear.</p>
                <input value={forceConfirmText} onChange={e => setForceConfirmText(e.target.value.toUpperCase())}
                  placeholder='Digite "CONFIRMAR" para liberar'
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mb-2 uppercase tracking-wider"
                  data-testid="force-confirm-input" />
                <button onClick={() => handleEndMeeting(pendingModal.meetingId, true)}
                  disabled={forceConfirmText !== 'CONFIRMAR' || meetingLoading}
                  data-testid="force-end-meeting-btn"
                  className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2">
                  <Square className="w-4 h-4" /> Forçar Encerramento
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Volunteer Check-in Modal (child without parent) */}
      {volunteerModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="volunteer-modal">
          <Card className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto shadow-2xl">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-amber-600" />
                <h3 className="font-nunito font-bold text-lg text-[#1A4D2E]">Check-in desacompanhado</h3>
              </div>
              <button onClick={() => { setVolunteerModal(false); setSelectedChildForVol(''); setVolObs(''); setChildSearch(''); }}
                className="text-gray-400 hover:text-gray-600" data-testid="close-volunteer-modal">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-gray-500">Você ficará como responsável por esta criança durante o encontro.</p>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Buscar criança</label>
                <input value={childSearch} onChange={e => setChildSearch(e.target.value)}
                  placeholder="Digite o nome ou código"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm"
                  data-testid="vol-child-search" />
              </div>
              <div className="max-h-60 overflow-y-auto border border-gray-100 rounded-xl">
                {childrenList
                  .filter(c => !childSearch.trim() || (c.nome || '').toLowerCase().includes(childSearch.toLowerCase()) || (c.codigo || '').toLowerCase().includes(childSearch.toLowerCase()))
                  .slice(0, 50)
                  .map(c => (
                    <button key={c.id} onClick={() => setSelectedChildForVol(c.id)} data-testid={`vol-child-${c.id}`}
                      className={`w-full text-left px-3 py-2 text-sm border-b border-gray-50 last:border-b-0 transition-all ${selectedChildForVol === c.id ? 'bg-[#F4D35E] font-bold' : 'hover:bg-gray-50'}`}>
                      <span className="text-[#1A4D2E]">{c.nome}</span>
                      <span className="text-[10px] text-gray-400 ml-2">{c.codigo} · {c.idade}a</span>
                    </button>
                  ))}
                {childrenList.length === 0 && (
                  <p className="text-center py-4 text-xs text-gray-400">Nenhuma criança cadastrada</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Observação (opcional)</label>
                <input value={volObs} onChange={e => setVolObs(e.target.value)}
                  placeholder="Ex: veio com o vizinho"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm"
                  data-testid="vol-obs" />
              </div>
              <button onClick={handleVolunteerGenerate} disabled={!selectedChildForVol}
                data-testid="vol-generate-btn"
                className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-white py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2">
                <UserCheck className="w-4 h-4" /> Confirmar Check-in
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CheckinVoluntario;
